import type { ActionDefinition } from '@command-center/action-sdk';

export type PortalAppType = 'admin' | 'client';
export type PortalModuleStatus = 'active' | 'disabled' | 'coming-soon';

export type PortalModuleComponent<TProps = Record<string, unknown>> = (props: TProps) => unknown;

export interface PortalModule<TProps = Record<string, unknown>> {
  id: string;
  label: string;
  route: string;
  icon?: string;
  navGroup?: string;
  component?: PortalModuleComponent<TProps>;
  actions?: ActionDefinition[];
  requiredConnectors?: string[];
  appTypes?: PortalAppType[];
  status?: PortalModuleStatus;
}

export interface RemotePortalModule {
  id: string;
  label: string;
  route: string;
  remoteEntryUrl: string;
  authRequired: true;
  requiredScopes: string[];
  status: 'disabled';
}

export interface ModuleRegistry {
  all: () => PortalModule[];
  enabledFor: (appType: PortalAppType) => PortalModule[];
  get: (id: string) => PortalModule | undefined;
  register: (module: PortalModule) => PortalModule;
}

export function createPortalModule<TProps = Record<string, unknown>>(
  module: PortalModule<TProps>,
): PortalModule<TProps> {
  return {
    status: 'active',
    ...module,
    route: normalizeModuleRoute(module.route),
  };
}

export function createDisabledRemoteModule(module: Omit<RemotePortalModule, 'status'>): RemotePortalModule {
  return { ...module, status: 'disabled' };
}

export function createModuleRegistry(modules: PortalModule[] = []): ModuleRegistry {
  const entries = new Map(modules.map((module) => [module.id, module]));

  return {
    all: () => Array.from(entries.values()),
    enabledFor: (appType) =>
      Array.from(entries.values()).filter((module) => {
        const status = module.status ?? 'active';
        const appTypes = module.appTypes ?? ['admin', 'client'];
        return status === 'active' && appTypes.includes(appType);
      }),
    get: (id) => entries.get(id),
    register: (module) => {
      if (entries.has(module.id)) {
        throw new Error(`Module already registered: ${module.id}`);
      }
      const normalized = createPortalModule(module);
      entries.set(normalized.id, normalized);
      return normalized;
    },
  };
}

function normalizeModuleRoute(route: string): string {
  const trimmed = route.trim();
  if (!trimmed) throw new Error('Module route cannot be empty.');
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}
